/**
 * Reconnect ActionCable when the shell returns to the foreground.
 *
 * Android WebViews suspend/throttle JS (and the underlying WebSocket) while
 * backgrounded, and @rails/actioncable's stale-connection monitor doesn't run
 * while the JS context is suspended. So on return the consumer can hold a
 * TCP-dead socket that still looks open, and live updates (chat messages, unread
 * badges) silently stop until a full page reload. resume_refresh only reloads
 * after a long (30 min) absence, so shorter switches leave the socket dead.
 *
 * Force a reopen on every foreground return so subscriptions resume without a
 * reload. reopen() is safe when already connected (ActionCable closes and
 * re-opens), and re-subscribes existing subscriptions automatically. No-op
 * outside the Capacitor shell; never throws.
 *
 * @capacitor/app is loaded lazily (dynamic import) so it is excluded from the
 * main entry bundle and never downloaded/parsed in MobiLoud or plain web.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";
import consumer from "../channels/consumer";

let registered = false;

function reportError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_cable_reconnect" },
    });
  } catch {
    // reporting must never break the app
  }
}

function handleStateChange({ isActive }) {
  if (!isActive) {
    return;
  }
  try {
    // Nothing to recover if no channels are subscribed on this screen — don't
    // open a socket just to idle (avoids connect/auth churn + server load).
    if (!consumer.subscriptions?.subscriptions?.length) {
      return;
    }
    consumer.connection.reopen();
  } catch (error) {
    reportError(error);
  }
}

/**
 * Start reopening ActionCable on foreground return. No-op off-shell; runs once;
 * never throws.
 */
export function registerCableReconnect() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  import("@capacitor/app")
    .then(({ App }) => {
      try {
        const result = App.addListener("appStateChange", handleStateChange);
        if (result && typeof result.catch === "function") {
          result.catch((error) => {
            registered = false; // async registration failed — allow a retry
            reportError(error);
          });
        }
      } catch (error) {
        registered = false; // allow a later call to retry
        reportError(error);
      }
    })
    .catch((error) => {
      registered = false; // allow a later call to retry
      reportError(error);
    });
}
